import { DemoInsights, DemoMatch } from 'types/demos';

const pickWinner = (match: DemoMatch) => {
    const [home, away] = match.teams;
    return home.score >= away.score ? home : away;
};

export const calculateDemoInsights = (matches: DemoMatch[]): DemoInsights => {
    if (!matches || matches.length === 0) {
        return {
            totalMatches: 0,
            avgDurationMinutes: null,
            recentWinners: []
        };
    }

    const durations = matches
        .map((match) => match.durationMinutes)
        .filter((value): value is number => typeof value === 'number');

    const avgDurationMinutes =
        durations.length > 0 ? Math.round((durations.reduce((sum, value) => sum + value, 0) / durations.length) * 10) / 10 : null;

    const mapFrequency = matches.reduce<Record<string, number>>((acc, match) => {
        acc[match.map] = (acc[match.map] ?? 0) + 1;
        return acc;
    }, {});

    const mostPlayedMap = Object.entries(mapFrequency)
        .sort(([, a], [, b]) => b - a)
        .map(([map, count]) => ({ map, count }))[0];

    const winCounts = matches.reduce<Record<string, number>>((acc, match) => {
        const winner = pickWinner(match);
        acc[winner.name] = (acc[winner.name] ?? 0) + 1;
        return acc;
    }, {});

    const hottestStreak = Object.entries(winCounts)
        .sort(([, a], [, b]) => b - a)
        .map(([team, wins]) => ({ team, wins }))[0];

    const recentWinners = matches
        .slice()
        .sort((a, b) => new Date(b.playedAt).getTime() - new Date(a.playedAt).getTime())
        .slice(0, 3)
        .map((match) => {
            const winner = pickWinner(match);
            return {
                matchId: match.id,
                team: winner.name,
                playedAt: match.playedAt,
            };
        });

    const lastUpdated = matches
        .map((match) => new Date(match.playedAt).getTime())
        .reduce((latest, current) => (current > latest ? current : latest), 0);

    return {
        totalMatches: matches.length,
        avgDurationMinutes,
        mostPlayedMap,
        lastUpdated: lastUpdated ? new Date(lastUpdated).toISOString() : undefined,
        hottestStreak,
        recentWinners,
    };
};
